// Remontée des erreurs d'écriture Supabase vers l'interface.
//
// Les providers métier écrivent réellement dans Supabase. En cas d'échec
// (RLS, contrainte, réseau), on ne doit JAMAIS afficher un faux succès : la
// modification optimiste est annulée et une erreur est publiée pour être
// affichée à l'utilisateur via le composant DataErrorToaster.

export type DataNoticeTone = "error" | "success";

export interface DataErrorDetail {
  /** Module concerné (ex. « immeubles », « documents »). */
  scope: string;
  /** Message compréhensible affiché à l'utilisateur. */
  message: string;
  /** Détail technique (RLS, contrainte…) conservé pour le diagnostic. */
  technical?: string;
  /** Ton du message ; « error » par défaut (compatibilité ascendante). */
  tone?: DataNoticeTone;
}

export const DATA_ERROR_EVENT = "immo:data-error";

export function dispatchDataError(detail: DataErrorDetail): void {
  // Journal technique utile au diagnostic.
  console.error(`[Supabase:${detail.scope}] ${detail.message}`, detail.technical ?? "");
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<DataErrorDetail>(DATA_ERROR_EVENT, { detail }));
  }
}

/**
 * Publie un message informatif (ex. confirmation d'action réussie) affiché à
 * l'utilisateur via le même composant que les erreurs. Utilisé pour ne jamais
 * annoncer un succès partiel : le texte doit refléter le résultat réel du serveur.
 */
export function dispatchNotice(detail: Omit<DataErrorDetail, "technical">): void {
  console.info(`[Notice:${detail.scope}] ${detail.message}`);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<DataErrorDetail>(DATA_ERROR_EVENT, { detail }));
  }
}

/**
 * Codes d'erreur Postgres correspondant à une règle MÉTIER levée explicitement
 * par un trigger ou une fonction (ex. « Limite de formule atteinte »). Le
 * message associé est déjà rédigé pour l'utilisateur : on peut l'afficher tel
 * quel, au lieu du message générique du provider.
 */
const BUSINESS_ERROR_CODES = new Set(["P0001"]);

/**
 * Retourne le message « métier » d'une erreur Supabase lorsqu'il existe et
 * qu'il est destiné à l'utilisateur (ex. « Limite de votre formule atteinte
 * pour « immeubles » (3 / 3)… »). Sinon, renvoie le message générique fourni
 * par le provider. Les erreurs purement techniques (RLS, contrainte) gardent
 * donc leur libellé générique.
 */
export function resolveWriteErrorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  const record = error as { message?: string; code?: string };
  if (typeof record.code === "string" && BUSINESS_ERROR_CODES.has(record.code)) {
    const message = (record.message ?? "").trim();
    if (message) return message;
  }
  return fallback;
}

/** Extrait un message lisible depuis une erreur Supabase/PostgREST. */
export function describeSupabaseError(error: unknown): string {
  if (!error) return "";
  if (typeof error === "string") return error;
  const record = error as { message?: string; details?: string; hint?: string; code?: string };
  const parts = [record.message, record.details, record.hint].filter(Boolean);
  const base = parts.join(" — ");
  if (base) return record.code ? `${base} (${record.code})` : base;
  return "Erreur inconnue";
}